import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema, generateArticleSchema } from "@/lib/jsonld";

import coverImage from "@/assets/blog/push-vs-pull-marketing/couverture.webp";
import spectreImage from "@/assets/blog/push-vs-pull-marketing/spectre-push-pull-canaux-marketing.webp";
import parcoursImage from "@/assets/blog/push-vs-pull-marketing/parcours-client-push-pull-sequence.webp";
import pullPushImage from "@/assets/blog/push-vs-pull-marketing/pull-alimente-push-strategie-marketing.webp";

const PushVsPullMarketing = () => {
  const jsonLdSchemas = [
    generateWebPageSchema(
      "Push vs pull marketing : différences, avantages et comment choisir en 2026",
      seoConfig.blogPushVsPullMarketing.description,
      "/push-vs-pull-marketing"
    ),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: "Push vs pull marketing", path: "/push-vs-pull-marketing" }
    ]),
    generateArticleSchema(
      "Push vs pull marketing : différences, avantages et comment choisir en 2026",
      seoConfig.blogPushVsPullMarketing.description,
      "https://unotif.com/push-vs-pull-marketing",
      "2026-05-03"
    )
  ];

  const imageStyle = {
    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
  };

  const pText = "text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4";
  const h2 = "text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.blogPushVsPullMarketing} jsonLd={jsonLdSchemas} />
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-sm font-medium text-[#495456] mb-6">
              <Link to="/blog" className="text-[#0c97a9] hover:underline">Blog</Link>
              <span className="mx-2">›</span>
              <span className="text-[#074155]">Push vs pull marketing</span>
            </nav>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-8">
              Push vs pull marketing : différences, avantages et comment choisir en 2026
            </h1>

            <img
              src={coverImage}
              alt="Comparaison entre push et pull marketing avec stratégies pour attirer ou contacter les clients selon le contexte"
              loading="eager"
              className="rounded-2xl w-full h-auto"
              style={imageStyle}
            />
          </div>
        </section>

        {/* Article */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Intro */}
            <p className={pText}>
              <strong className="font-semibold">Push et pull marketing</strong> : deux termes qu'on entend souvent ensemble, rarement bien définis, et encore plus rarement bien utilisés. L'un initie le contact, l'autre attire. L'un va vers le consommateur, l'autre fait venir le consommateur vers lui.
            </p>
            <p className={pText}>
              Ces deux logiques ne s'opposent pas. Elles se séquencent. La vraie question n'est pas "lequel est meilleur ?" mais "lequel sert mon objectif à cette étape précise de ma relation avec mon client ?" Cet article pose les définitions claires, liste les canaux de chaque approche, et donne une grille de décision pratique pour choisir sans se tromper. Pour une vision complète du push marketing et de ses canaux, notre guide complet sur le <Link to="/push-marketing" className="text-[#0c97a9] hover:underline">push marketing</Link> pose toutes les bases.
            </p>

            {/* Section 1 */}
            <h2 className={h2}>Push marketing et pull marketing : les définitions claires</h2>
            <p className={pText}>Avant de comparer, il faut définir avec précision.</p>
            <p className={pText}>
              Le <strong className="font-semibold">push marketing</strong> est une initiative de l'entreprise vers le consommateur. L'entreprise choisit le moment, le message, le canal. Elle envoie sans attendre que le consommateur en fasse la demande. Le consommateur reçoit sans avoir cherché. Exemples : un SMS promotionnel, une notification push wallet, un email avec une offre de la semaine, une publicité display sur un site qu'on visite.
            </p>
            <p className={pText}>
              Le <strong className="font-semibold">pull marketing</strong> est une stratégie d'attraction. L'entreprise crée les conditions pour être trouvée au moment où le consommateur cherche activement. Elle répond à une intention existante plutôt que de la créer. Exemples : un article de blog bien positionné sur Google, une vidéo YouTube qui répond à une question, une annonce Google Ads déclenchée par une requête à forte intention.
            </p>
            <p className={pText}>
              La formule la plus utile pour retenir la distinction : en push, vous interrompez. En pull, vous êtes là quand on vous cherche.
            </p>
            <p className={pText}>
              Cette distinction n'implique aucun jugement de valeur. Interrompre avec un message pertinent au bon moment est infiniment plus efficace qu'être présent sur Google si personne ne cherche ce que vous proposez. Et être bien positionné sur Google ne sert à rien si le visiteur attiré repart sans laisser de contact.
            </p>

            {/* Section 2 */}
            <h2 className={h2}>Les canaux push vs les canaux pull</h2>
            <p className={pText}>
              Chaque approche correspond à une famille de canaux distincte. Certains canaux sont clairement dans un camp. D'autres sont hybrides selon l'usage qu'on en fait.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse rounded-xl overflow-hidden text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold"></th>
                    <th className="px-4 py-3 text-left font-semibold">Canaux push</th>
                    <th className="px-4 py-3 text-left font-semibold">Canaux pull</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Communication directe</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Email, SMS, notification push, push wallet</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">—</td>
                  </tr>
                  <tr className="border-b border-border bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Publicité</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Display, programmatique, prospection à froid</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">SEM sur intention (Google Ads), retargeting</td>
                  </tr>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Contenu</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Publications non sollicitées, stories sponsorisées</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Blog, SEO, YouTube, podcast, FAQ</td>
                  </tr>
                  <tr className="bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Réseaux sociaux</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Message direct, publicité sociale ciblée</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Contenu organique qui répond à des recherches</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className={pText}>Quelques précisions importantes sur les cas hybrides.</p>
            <p className={pText}>
              Le <strong className="font-semibold">retargeting</strong> est un outil push (l'annonce arrive sans demande de l'utilisateur), mais il est déclenché par un comportement pull préalable (l'utilisateur a visité le site). C'est du push comportemental : il s'appuie sur une intention détectée.
            </p>
            <p className={pText}>
              L'<strong className="font-semibold">email</strong> est push par nature (l'entreprise envoie), mais à une base qui a accepté de recevoir (opt-in). Ce consentement nuance son caractère intrusif et le rapproche d'un push "permis", selon le terme de Seth Godin.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              Le <strong className="font-semibold">SEO</strong> est du pull pur : vous créez un contenu qui répond à une requête, l'utilisateur vient vous chercher. Aucune initiative de contact de votre part au moment de la découverte.
            </p>

            <img
              src={spectreImage}
              alt="Positionnement des canaux marketing sur le spectre push pull avec SMS push wallet email retargeting SEM et SEO"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Section 3 */}
            <h2 className={h2}>Quand choisir le push marketing</h2>
            <p className={pText}>
              Le <strong className="font-semibold">push marketing</strong> est le bon levier quand vous avez quelque chose à dire à quelqu'un qui vous connaît déjà et qui a accepté de vous entendre. Il performe dans des situations précises.
            </p>
            <p className={pText}>
              La <strong className="font-semibold">réactivation d'un client inactif</strong> est le cas d'usage le plus évident. Un client qui n'est pas revenu depuis 30 jours ne cherche pas activement votre offre. Le pull ne peut pas le toucher : il faut aller le chercher. Un message personnalisé ("Ça fait 3 semaines, voici quelque chose rien que pour vous") sur un canal direct produit un taux de retour 3 à 4 fois supérieur à n'importe quelle action pull.
            </p>
            <p className={pText}>
              Les <strong className="font-semibold">offres à durée limitée ou événements à date fixe</strong> nécessitent un contact direct. L'urgence ne peut pas attendre que l'utilisateur vienne chercher l'information. Le push crée la fenêtre d'attention au bon moment.
            </p>
            <p className={pText}>
              Les <strong className="font-semibold">annonces de nouveautés à une base existante</strong> sont naturellement push. Vos clients ont déjà un intérêt pour vous. Leur envoyer une notification le jour où vous lancez quelque chose de nouveau est pertinent et attendu.
            </p>
            <p className={pText}>
              Les <strong className="font-semibold">rappels de valeur accumulée</strong> (points de fidélité, avantage expirant, prochain palier) ne peuvent pas être gérés en pull. Le timing est clé : le message doit arriver quand la donnée est pertinente, pas quand l'utilisateur cherche l'information. Un push wallet envoyé automatiquement quand le solde de points d'un client approche d'un seuil de récompense produit des résultats immédiats.
            </p>
            <p className={pText}>
              La limite du push seul : il ne fonctionne qu'avec une base opt-in qualifiée. Sans elle, le push devient de la publicité non ciblée, coûteuse et peu efficace. L'acquisition de nouvelles audiences se fait rarement en push seul.
            </p>

            {/* Section 4 */}
            <h2 className={h2}>Quand choisir le pull marketing</h2>
            <p className={pText}>
              Le <strong className="font-semibold">pull marketing</strong> est le bon levier quand votre cible cherche activement une réponse à un problème que vous pouvez résoudre. Il excelle dans des contextes spécifiques.
            </p>
            <p className={pText}>
              L'<strong className="font-semibold">acquisition de nouvelles audiences</strong> qui ne vous connaissent pas est son domaine de prédilection. Un consommateur qui tape "meilleur outil de fidélisation PME" sur Google est en mode décision. S'il tombe sur votre article, vous n'avez pas eu à le chercher : c'est lui qui est venu. La confiance initiale est plus élevée qu'avec une publicité push non sollicitée.
            </p>
            <p className={pText}>
              La <strong className="font-semibold">construction de crédibilité dans la durée</strong> est un autre avantage du pull. Un blog bien tenu sur 12 à 18 mois, une présence YouTube qui répond aux questions de votre secteur, une présence SEO sur des mots-clés à forte intention : ces actifs se constituent lentement mais produisent des résultats durables et non dépendants d'un budget publicitaire.
            </p>
            <p className={pText}>
              Les <strong className="font-semibold">marchés où le consommateur fait une recherche active avant l'achat</strong> sont des contextes pull par excellence. En B2B, en services complexes, en formation : l'acheteur compare, lit, se renseigne avant de décider. Être présent à cette étape de recherche est souvent plus décisif que d'interrompre sa journée avec un message non sollicité.
            </p>
            <p className={pText}>
              La limite du pull seul est structurelle : une fois le visiteur attiré, s'il repart sans laisser de contact, vous n'avez aucun moyen de le relancer. Le pull attire. Le push retient. Les structures qui n'investissent que dans le pull ont des coûts d'acquisition structurellement élevés, car elles recommencent l'effort d'attraction à chaque cycle.
            </p>

            {/* Section 5 */}
            <h2 className={h2}>La vraie stratégie push pull : séquencer selon l'étape du cycle client</h2>
            <p className={pText}>
              La <strong className="font-semibold">stratégie push pull</strong> la plus efficace n'est pas un choix entre les deux approches : c'est un séquençage précis selon l'étape du cycle de vie client.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse rounded-xl overflow-hidden text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold">Étape</th>
                    <th className="px-4 py-3 text-left font-semibold">Approche</th>
                    <th className="px-4 py-3 text-left font-semibold">Canal type</th>
                    <th className="px-4 py-3 text-left font-semibold">Objectif</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Découverte (inconnu)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Pull</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">SEO, blog, YouTube</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Être trouvé</td>
                  </tr>
                  <tr className="border-b border-border bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Première visite (visiteur)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Pull + capture opt-in</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Formulaire, QR code wallet</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Créer le contact</td>
                  </tr>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Conversion (prospect chaud)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Push ciblé</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Email, notification push, wallet</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Déclencher l'achat</td>
                  </tr>
                  <tr className="border-b border-border bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Fidélisation (client actif)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Push personnalisé</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Push wallet, SMS, email segmenté</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Faire revenir</td>
                  </tr>
                  <tr className="border-b border-border bg-white">
                    <td className="px-4 py-3 font-medium text-[#495456]">Réactivation (client dormant)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Push direct</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Push wallet, SMS</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Récupérer avant la perte</td>
                  </tr>
                  <tr className="bg-[#f8fafa]">
                    <td className="px-4 py-3 font-medium text-[#495456]">Ambassadeur</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Push + pull</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Parrainage (push) + avis clients (pull)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Activer le bouche-à-oreille</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className={pText}>
              Ce tableau révèle une logique simple : le pull travaille sur les étapes où l'initiative vient du consommateur (il cherche, il découvre). Le push travaille sur les étapes où l'initiative doit venir de vous (vous relancez, vous animez, vous réactivez).
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              La plupart des petits commerces et entrepreneurs investissent uniquement dans le pull (réseaux sociaux, bouche-à-oreille naturel) ou uniquement dans le push (email de masse sans segmentation). Les deux approches isolées sous-performent. Séquencées, elles se renforcent.
            </p>

            <img
              src={parcoursImage}
              alt="Parcours client en 6 étapes avec séquence push pull de la découverte à ambassadeur incluant conversion et fidélisation"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Section 6 */}
            <h2 className={h2}>Les 3 erreurs classiques dans une stratégie push pull</h2>
            <p className={pText}>
              Connaître la distinction théorique entre push et pull ne suffit pas. Les erreurs les plus fréquentes tiennent à l'exécution, pas à la compréhension.
            </p>
            <p className={pText}>
              <strong className="font-semibold">Erreur 1 : tout miser sur le pull sans mécanisme de capture.</strong> Beaucoup de structures investissent dans le contenu, le SEO, les réseaux sociaux, et n'ont aucun moyen de recontacter les visiteurs attirés. Un article bien positionné amène 500 visiteurs par mois. Si 498 repartent sans laisser de contact, l'effort pull ne se transforme jamais en base push. La conséquence : un coût d'acquisition structurellement élevé, car chaque cycle recommence à zéro.
            </p>
            <p className={pText}>
              <strong className="font-semibold">Erreur 2 : tout miser sur le push sans base opt-in qualifiée.</strong> Envoyer des SMS à des listes achetées ou des emails à des contacts qui n'ont pas consenti : coût réglementaire (RGPD), réputation expéditeur abîmée, taux de conversion proche de zéro. Le push n'a de valeur que sur des contacts qui ont accepté de vous entendre.
            </p>
            <p className={pText}>
              <strong className="font-semibold">Erreur 3 : traiter push et pull comme deux budgets séparés avec des objectifs séparés.</strong> Ces deux approches sont les deux phases d'un même parcours client. Les métriques doivent se parler : combien de visiteurs pull se transforment en contacts push ? Combien de contacts push deviennent des clients actifs ? Un tableau de bord qui ne relie pas ces deux flux manque l'essentiel.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-8">
              Pour approfondir le fonctionnement des canaux push et comprendre comment maximiser l'impact de vos messages une fois la base constituée, notre guide sur les notifications push et leur taux d'ouverture entre dans le détail des formats et des bonnes pratiques.
            </p>

            <img
              src={pullPushImage}
              alt="Relation entre pull et push marketing avec génération de leads via pull et animation des contacts via push"
              loading="lazy"
              className="rounded-2xl w-full h-auto mb-12"
              style={imageStyle}
            />

            {/* Conclusion */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4 mt-12">
              Choisir entre <strong className="font-semibold">push et pull marketing</strong> n'est pas la bonne question. La question est : à quelle étape de votre relation client vous trouvez-vous, et quel levier sert cet objectif ? Le pull construit votre présence et attire des inconnus. Le push anime votre base et génère des retours mesurables. Un programme de fidélisation solide vit en push : il ne dépend pas des algorithmes, communique au moment choisi, et rentabilise l'effort pull investit en amont. Unotif est un outil push natif, conçu pour animer le segment le plus engagé de votre base.
            </p>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PushVsPullMarketing;
